import { formatCurrency, formatIsoDate } from '@if/core/utils'
import { Table, TableBody, TableCell, TableRow, Typography } from '@mui/material'
import TableCellNoWrap from '@src/components/Common/Table/TableCellNoWrap'
import { CalendarSimulation } from '@src/types/loan/loan-simulation'
import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { getColorForDifference, useLoanCalendarActionPreviewData } from './LoanCalendarActionPreview-hook'

const LoanCalendarActionPreviewTableHeader = memo(({ title }: { title: string }) => {
  const { t } = useTranslation()
  return (
    <TableRow>
      <TableCell>
        <Typography variant="subtitle2">{title}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="subtitle2">{t('common.before')}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="subtitle2">{t('common.after')}</Typography>
      </TableCell>
      <TableCell />
    </TableRow>
  )
})
LoanCalendarActionPreviewTableHeader.displayName = 'LoanCalendarActionPreviewTableHeader'

interface LoanCalendarActionPreviewTableProps {
  simulationBefore: CalendarSimulation
  simulationAfter: CalendarSimulation
  actionDate?: Date
}

const LoanCalendarActionPreviewTable = ({
  simulationBefore,
  simulationAfter,
  actionDate,
}: LoanCalendarActionPreviewTableProps) => {
  const { t } = useTranslation()
  const beforeOp = simulationBefore.summaryInformation
  const afterOp = simulationAfter.summaryInformation
  const {
    lastPaymentDateBefore,
    lastPaymentDateAfter,
    lastPaymentDateDifference,
    lastTotalInterestPaidBefore,
    lastTotalInterestPaidAfter,
    totalInterestPaidDifference,
  } = useLoanCalendarActionPreviewData(simulationBefore, simulationAfter, actionDate)

  return (
    <Table size="small">
      <TableBody>
        <LoanCalendarActionPreviewTableHeader
          title={t('loan.payment.preview.projectedDelay', {
            date: formatIsoDate(new Date()),
          })}
        />
        <TableRow>
          <TableCell>{t('loan.payment.preview.nbLateDays')}</TableCell>
          <TableCellNoWrap>{t('common.days', { count: beforeOp?.daysLate || 0 })}</TableCellNoWrap>
          <TableCellNoWrap>
            <Typography variant="inherit" style={{ fontWeight: 'bold' }}>
              {t('common.days', { count: afterOp?.daysLate || 0 })}
            </Typography>
          </TableCellNoWrap>
          <TableCell />
        </TableRow>
        <LoanCalendarActionPreviewTableHeader title={t('loan.payment.preview.paymentsEnd')} />
        <TableRow>
          <TableCell>{t('loan.payment.preview.lastPayment')}</TableCell>
          <TableCellNoWrap>{formatIsoDate(lastPaymentDateBefore)}</TableCellNoWrap>
          <TableCellNoWrap>
            <Typography
              variant="inherit"
              style={{ fontWeight: 'bold' }}
              color={getColorForDifference(lastPaymentDateBefore.getTime(), lastPaymentDateAfter.getTime())}
            >
              {formatIsoDate(lastPaymentDateAfter)}
            </Typography>
          </TableCellNoWrap>
          <TableCellNoWrap>{lastPaymentDateDifference}</TableCellNoWrap>
        </TableRow>
        <TableRow>
          <TableCell>{t('loan.payment.preview.totalInterestPaid')}</TableCell>
          <TableCellNoWrap>{formatCurrency(lastTotalInterestPaidBefore)}</TableCellNoWrap>
          <TableCellNoWrap>
            <Typography
              variant="inherit"
              style={{ fontWeight: 'bold' }}
              color={getColorForDifference(lastTotalInterestPaidBefore, lastTotalInterestPaidAfter)}
            >
              {formatCurrency(lastTotalInterestPaidAfter)}
            </Typography>
          </TableCellNoWrap>
          <TableCellNoWrap>
            <Typography variant="inherit" color={getColorForDifference(0, totalInterestPaidDifference)}>
              {formatCurrency(totalInterestPaidDifference)}
            </Typography>
          </TableCellNoWrap>
        </TableRow>
      </TableBody>
    </Table>
  )
}

export default memo(LoanCalendarActionPreviewTable)
